import type { FC, ReactNode } from 'react'
import { useSocialAccount } from '@g4g/social-account/components/SocialAccountProvider'
import LoginButton from '../../shared/LoginButton'
import RecipientRadioOpt from '../RecipientRadioOpt'
import useRecipientUsers from '../hooks/useRecipientUsers'
import { useRecipient } from '../RecipientProvider'
import RecipientOnboarding from './RecipientOnboarding'
import LgBreakpoint from '@g4g/ui/src/utility/LgBreakpoint'
import Spinner from '@g4g/ui/src/internal/Spinner'
import RecipientError from '../RecipientError'

const LoginSection: FC<{ loading?: boolean; children?: ReactNode }> = ({ children, loading }) => (
  <div>
    <div className="mb-2 text-sm text-white/50">My Account</div>
    {loading ? (
      <div className="flex justify-center w-full">
        <Spinner className="w-6 h-6 text-white" delay={500} />
      </div>
    ) : (
      <>{children}</>
    )}
  </div>
)

const LoggedOut: FC = () => {
  const { recentRecipients } = useRecipient()

  if (recentRecipients.length === 0) {
    return <LgBreakpoint max={<RecipientOnboarding />} />
  }

  return (
    <LoginSection>
      <div className="lg:w-[300px]">
        <LoginButton primary />
      </div>
    </LoginSection>
  )
}

const LoggedIn: FC<{ cosId: string }> = ({ cosId }) => {
  const { recentRecipients, error, loading } = useRecipientUsers()
  const loggedInUser = recentRecipients?.find((r) => r.cosId === cosId)

  if (loading) {
    return <LoginSection loading={loading} />
  }

  if (error || !recentRecipients || !loggedInUser) {
    return (
      <LoginSection>
        <RecipientError message="Failed to retrieve account information" />
      </LoginSection>
    )
  }

  return (
    <LoginSection>
      <div className="w-full">
        <RecipientRadioOpt recipient={loggedInUser} />
      </div>
    </LoginSection>
  )
}

const RecipientLogin: FC = () => {
  const { socialAccount } = useSocialAccount()

  if (!socialAccount?.userData.userId) {
    return <LoggedOut />
  }

  return <LoggedIn cosId={socialAccount.userData.userId} />
}

export default RecipientLogin
